import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { Toast } from 'flowbite-react';
import { IconType } from 'react-icons';
import { MdCheck, MdError, MdOutlineReportGmailerrorred, MdWarningAmber } from 'react-icons/md';
import classNames from 'classnames';
import { Slide } from '@mui/material';
import { IoClose } from 'react-icons/io5';

const ToastContext = createContext<(option: ToastOptions) => void>(() => {});

interface ToastProviderProps {
	children: React.ReactNode;
}

type ToastType = 'success' | 'error' | 'info' | 'warning';
type ToastOptions = {
	type?: ToastType;
	duration?: number;
	title: String;
	htmlTitle?: ReactNode;
};

const toastIcon: Record<ToastType, IconType> = {
	success: MdCheck,
	error: MdOutlineReportGmailerrorred,
	info: MdError,
	warning: MdWarningAmber,
};
const ToastProvider = ({ children }: ToastProviderProps) => {
	const [showToast, setShowToast] = useState(false);
	const [duration, setDuration] = useState<number>(3000);
	const [htmlTitle, setHtmlTitle] = useState<ReactNode>(null);
	const [title, setTitle] = useState<String>('');
	const [type, setType] = useState<ToastType>('success');

	const ToastIcon = toastIcon[type];
	const closeToast = () => {
		setShowToast(false);
		setDuration(3000);
		setTitle('');
		setHtmlTitle(null);
		setType('success');
	};
	const toast = (option: ToastOptions) => {
		setShowToast(true);
		setTitle(option.title);
		if (option.htmlTitle) setHtmlTitle(option.htmlTitle);
		if (option.duration) setDuration(option.duration);
		if (option.type) setType(option.type);
	};

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;
		if (showToast) {
			timeoutId = setTimeout(closeToast, type === 'error' ? 7000 : duration);
		}

		return () => {
			clearTimeout(timeoutId);
		};
	}, [showToast, duration, type]);
	return (
		<>
			<ToastContext.Provider value={toast}>
				{children}

				<Slide in={showToast} direction="left" mountOnEnter unmountOnExit>
					<div className="fixed top-4 right-4 w-[300px] z-[51]">
						<Toast
							className={classNames('border', {
								'border-green-400': type === 'success',
								'border-red-400 ': type === 'error',
								'border-yellow-400 ': type === 'warning',
								'border-blue-400 ': type === 'info',
							})}
						>
							<div className="flex w-full relative justify-start items-center gap-2">
								<div
									className={classNames(
										'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#05A660] text-white',
										{
											'bg-green-500': type === 'success',
											'bg-red-500': type === 'error',
											'bg-yellow-500 ': type === 'warning',
											'bg-[#7777E7] ': type === 'info',
										},
									)}
								>
									<ToastIcon className="h-5 w-5" />
								</div>
								{htmlTitle ? (
									htmlTitle
								) : (
									<div className="text-sm font-normal text-gray-700 [word-break:break-word] dark:text-white">
										{title}
									</div>
								)}
								<div
									onClick={closeToast}
									className={classNames(
										'ml-auto cursor-pointer rounded-[4px] transition-opacity duration-[10000] hover:bg-gray-200',
									)}
								>
									<IoClose
										size={'24px'}
										className={classNames({
											'text-green-500': type === 'success',
											'text-red-500': type === 'error',
											'text-yellow-500': type === 'warning',
											'text-[#7777E7]': type === 'info',
										})}
									/>
								</div>
							</div>
						</Toast>
					</div>
				</Slide>
			</ToastContext.Provider>
		</>
	);
};

export default ToastProvider;

export const useToast = () => {
	const toast = useContext(ToastContext);
	return toast;
};
